import { sendLocal, loadMfeMessage } from "@sinch/bus";
import type { FC, ReactNode } from "react";
import { Fragment, useState } from "react";
import { useWindowSize } from "../../hooks";
import { MaxBreakPoints } from "../../style/constants";
import { MobileNavigation } from "./MobileNavigation";
import { NavigationBarPanel } from "./NavigationBarPanel/NavigationBarPanel";
import { Sidebar } from "./Sidebar";
import { SidebarItem } from "./SidebarItem";
import { SubMenuContent } from "./SubMenuContent";
import { NavigationHoverContextProvider } from "./contexts/NavigationHoverContext";
import { type NavigationConfig } from "./type";

type CommonProps = {
  config: NavigationConfig;
  topSection: ReactNode;
  profileItem?: ReactNode;
};

type NonMobileProps = CommonProps & {
  isMobileFriendly?: false;
};
type MobileProps = CommonProps & {
  isMobileFriendly: true;
  MobileProfileItem: FC<{ onClose: () => void }>;
  mobileLogo: ReactNode;
  mobileMessagingConfig: NavigationConfig;
  mobileSettingsConfig: NavigationConfig[number]["subNavigationConfig"];
};

export const NavigationBar: FC<NonMobileProps | MobileProps> = (props) => {
  const { config, topSection, profileItem, isMobileFriendly, ...mobileProps } =
    props;

  const [panelRef, setPanelRef] = useState<HTMLElement | null>(null);
  const { width } = useWindowSize();

  return (
    <>
      {!!isMobileFriendly &&
      width <= MaxBreakPoints.MEDIUM &&
      "MobileProfileItem" in mobileProps &&
      "mobileLogo" in mobileProps &&
      "mobileMessagingConfig" in mobileProps &&
      "mobileSettingsConfig" in mobileProps ? (
        <MobileNavigation
          MobileProfileItem={mobileProps.MobileProfileItem}
          mobileLogo={mobileProps.mobileLogo}
          mobileMessagingConfig={mobileProps.mobileMessagingConfig}
          mobileSettingsConfig={mobileProps.mobileSettingsConfig}
        />
      ) : (
        <NavigationHoverContextProvider panelNode={panelRef}>
          <Sidebar>
            <div>
              {topSection}
              {config
                .filter((navItem) => navItem.position === "top")
                .map((navItem) => (
                  <Fragment key={navItem.key}>
                    <SidebarItem
                      id={navItem.key}
                      key={navItem.key}
                      icon={navItem.icon}
                      onHover={() => sendLocal(loadMfeMessage(navItem.mfe))}
                      subLinks={
                        navItem.subNavigationConfig?.flatMap((subConf) =>
                          subConf.subRoutes.map((subRoute) => subRoute.href)
                        ) || []
                      }
                      text={navItem.label}
                      to={navItem.href}
                    >
                      {navItem.subNavigationConfig && (
                        <SubMenuContent
                          id={navItem.key}
                          key={`content-${navItem.key}`}
                          subNavigationConfig={navItem.subNavigationConfig}
                        />
                      )}
                    </SidebarItem>
                  </Fragment>
                ))}
            </div>
            <div>
              {config
                .filter((navItem) => navItem.position === "bottom")
                .map((navItem) => (
                  <Fragment key={navItem.key}>
                    <SidebarItem
                      id={navItem.key}
                      key={navItem.key}
                      icon={navItem.icon}
                      onHover={() => sendLocal(loadMfeMessage(navItem.mfe))}
                      subLinks={
                        navItem.subNavigationConfig?.flatMap((subConf) =>
                          subConf.subRoutes.map((subRoute) => subRoute.href)
                        ) || []
                      }
                      text={navItem.label}
                      to={navItem.href}
                    >
                      {navItem.subNavigationConfig && (
                        <SubMenuContent
                          id={navItem.key}
                          key={`content-${navItem.key}`}
                          subNavigationConfig={navItem.subNavigationConfig}
                        />
                      )}
                    </SidebarItem>
                  </Fragment>
                ))}
              {profileItem}
            </div>
          </Sidebar>
          <NavigationBarPanel ref={setPanelRef} />
        </NavigationHoverContextProvider>
      )}
    </>
  );
};
